/**
 * Sign-up requests: what visitors leave when the instance does not charge
 * online. Administration only, and deliberately NOT tenant-scoped — a
 * prospective client belongs to no tenant yet, so there is nothing to scope by.
 *
 * The tray opens on the pending ones, which are the only ones that need doing;
 * the state filter reaches the decided ones. The API always orders newest
 * first and offers no sorting, so no column advertises one.
 */

import { useMemo, useState } from 'react';
import {
  EmailField,
  FunctionField,
  List,
  TextField,
  useGetList,
  useLocaleState,
  useTranslate,
} from 'react-admin';
import { Chip, Tooltip } from '@mui/material';
import { CrudListActions, CustomDatagrid } from '@/components/common';
import { compactListSx } from '@/components/common/compactListSx';
import {
  CollapsibleFilterPanel,
  FilterExpandContext,
} from '@/components/common/CollapsibleFilterPanel';
import { CustomSelectInputFilter } from '@/components/common/CustomFilters';
import { SignupRequestActions } from './SignupRequestActions';

/** Longest note shown inline; the rest is available on hover. */
const NOTES_PREVIEW = 80;

/** Date formats follow the interface language, not a fixed locale. */
const DATE_LOCALES: Record<string, string> = { es: 'es-ES', en: 'en-GB' };

const STATE_COLORS: Record<string, 'warning' | 'success' | 'default'> = {
  pending: 'warning',
  converted: 'success',
  rejected: 'default',
};

export const SignupRequestList = () => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const dateLocale = DATE_LOCALES[locale] ?? locale;
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const filterExpandValue = useMemo(
    () => ({ expanded: filtersExpanded, setExpanded: setFiltersExpanded }),
    [filtersExpanded]
  );

  const { data: plans } = useGetList('plans', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'plan_id', order: 'ASC' },
  });
  const planNames = useMemo(() => {
    const map: Record<number, string> = {};
    (plans ?? []).forEach((plan: { plan_id: number; name: string }) => {
      map[plan.plan_id] = plan.name;
    });
    return map;
  }, [plans]);

  const filters = useMemo(
    () => [
      <CollapsibleFilterPanel key="filters" source="_cfp" alwaysOn alwaysVisibleCount={1}>
        <CustomSelectInputFilter
          source="state"
          label={translate('resources.signupRequests.fields.state')}
          choices={[
            { id: 'pending', name: translate('resources.signupRequests.states.pending') },
            { id: 'converted', name: translate('resources.signupRequests.states.converted') },
            { id: 'rejected', name: translate('resources.signupRequests.states.rejected') },
          ]}
          alwaysOn
        />
      </CollapsibleFilterPanel>,
    ],
    [translate]
  );

  return (
    <FilterExpandContext.Provider value={filterExpandValue}>
      <List
        sx={compactListSx}
        filters={filters}
        filterDefaultValues={{ state: 'pending' }}
        actions={<CrudListActions showCreate={false} />}
        sort={{ field: 'created_at', order: 'DESC' }}
        exporter={false}
      >
        <CustomDatagrid
          primarySource="full_name"
          secondarySource="email"
          bulkActionButtons={false}
        >
          <SignupRequestActions label="" sortable={false} />
          <TextField source="full_name" sortable={false} />
          <EmailField source="email" sortable={false} />
          <TextField source="company_name" sortable={false} />
          <TextField source="contact_no" sortable={false} />
          <FunctionField
            source="plan_id"
            sortable={false}
            render={(record: { plan_id?: number }) =>
              record?.plan_id ? planNames[record.plan_id] ?? String(record.plan_id) : '—'
            }
          />
          {/* The API answers the timestamp as `createdAt` — its own convention
              across every endpoint, not a quirk of this one. `created_at` is
              read as well only because the column is named that way in the
              database and a future raw query could surface it. */}
          <FunctionField
            source="created_at"
            sortable={false}
            render={(record: { created_at?: string; createdAt?: string }) => {
              const value = record?.createdAt ?? record?.created_at;
              return value ? new Date(value).toLocaleString(dateLocale) : '—';
            }}
          />
          <FunctionField
            source="state"
            sortable={false}
            render={(record: { state?: string }) => (
              <Chip
                size="small"
                variant="outlined"
                color={STATE_COLORS[record?.state ?? ''] ?? 'default'}
                label={translate(
                  `resources.signupRequests.states.${record?.state ?? 'pending'}`,
                  { _: record?.state ?? '' }
                )}
              />
            )}
          />
          <FunctionField
            source="notes"
            sortable={false}
            render={(record: { notes?: string | null }) => {
              const notes = record?.notes ?? '';
              if (!notes) return '—';
              return notes.length > NOTES_PREVIEW ? (
                <Tooltip title={notes}>
                  <span>{`${notes.slice(0, NOTES_PREVIEW)}…`}</span>
                </Tooltip>
              ) : (
                notes
              );
            }}
          />
        </CustomDatagrid>
      </List>
    </FilterExpandContext.Provider>
  );
};

export default SignupRequestList;
